// frontend/src/components/production-sequence/LineTabsBar.tsx
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_LINES } from '@/hooks/useProductionSequence';
import type { LineId } from '@/types/productionSequence';

interface Props {
  lines: LineId[];
  selectedLine: LineId;
  dark: boolean;
  onSelect: (line: LineId) => void;
}

/**
 * Barra de pestañas de líneas de envasado al estilo "hojas de Excel": una tira
 * horizontal fijada al pie de la pantalla con una pestaña por línea. Selección
 * ÚNICA: al hacer clic se activa esa línea (no hay "Todas" ni multi-selección).
 * Cuando las pestañas no caben, aparecen flechas ◀ ▶ para desplazar la tira hacia
 * el lado oculto (no usa scroll del navegador).
 */
export function LineTabsBar({ lines, selectedLine, dark, onSelect }: Props) {
  const { t } = useTranslation();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  const realLines = lines.filter(l => l !== ALL_LINES);

  // Recalcula si hay contenido oculto a izquierda/derecha.
  const updateOverflow = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setOverflow({
      left: scrollLeft > 1,
      right: scrollLeft + clientWidth < scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    updateOverflow();
    const el = scrollerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateOverflow, lines, selectedLine]);

  function scrollStep(dir: -1 | 1) {
    scrollerRef.current?.scrollBy({ left: dir * 240, behavior: 'smooth' });
  }

  const bar = dark ? 'bg-slate-900 border-white/8' : 'bg-slate-50 border-slate-200';
  const arrow = dark
    ? 'text-slate-300 hover:bg-white/10 border-white/10'
    : 'text-slate-500 hover:bg-slate-100 border-slate-200';
  const tabBase = 'shrink-0 text-[11px] font-medium px-3 py-1.5 rounded-md border transition-colors cursor-pointer whitespace-nowrap';
  const tabActive = 'bg-sky-500 border-sky-500 text-white shadow-sm';
  const tabIdle = dark
    ? 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'
    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50';

  function ArrowBtn({ dir, show }: { dir: -1 | 1; show: boolean }) {
    if (!show) return null;
    return (
      <button
        onClick={() => scrollStep(dir)}
        className={`shrink-0 flex items-center justify-center w-7 h-7 rounded-md border ${arrow}`}
        aria-label={dir < 0 ? t('sequence.lineTabs.scrollLeft') : t('sequence.lineTabs.scrollRight')}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8">
          {dir < 0 ? <path d="M7.5 2.5L4 6l3.5 3.5" /> : <path d="M4.5 2.5L8 6l-3.5 3.5" />}
        </svg>
      </button>
    );
  }

  return (
    <div className={`shrink-0 flex items-center gap-1.5 border-t px-2 py-2 ${bar}`}>
      <ArrowBtn dir={-1} show={overflow.left} />

      <div
        ref={scrollerRef}
        onScroll={updateOverflow}
        className="flex items-center gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: 'none' }}
      >
        {realLines.map(l => (
          <button
            key={l}
            onClick={() => onSelect(l)}
            className={`${tabBase} ${selectedLine === l ? tabActive : tabIdle}`}
          >
            {l}
          </button>
        ))}
      </div>

      <ArrowBtn dir={1} show={overflow.right} />
    </div>
  );
}
